// Back-office copy in the §13 voice (docs/brand/OFFICIAL/popcart-bold-pop-manual.html):
// "Own it in word one. Fix it in sentence two." Short words; one wink per message, max, and
// never on an error ("Jokes wait until it's fixed"). Pure, so node --test can check it.

/** The message the core client invents when the API sent no `error` string. */
export const STATUS_ONLY = /^Request failed \(\d+\)$/;

export const FALLBACK_ERROR = "That didn't go through. Try again.";

/**
 * A human message for an API error. The server's own `error` string wins (it is already written
 * for people); a status-only error (or a raw "Request failed (N)") gets voice copy instead.
 * @param {{ status?: number; code?: string; message?: string } | null | undefined} err
 * @param {string} [fallback]
 */
export function voiceError(err, fallback = FALLBACK_ERROR) {
  if (!err) return fallback;
  const { status, code, message } = err;
  if (code === 'forbidden') return "You don't have access to this.";
  if (status === 0 || code === 'network') return "Can't reach PopCart. Check your connection.";
  if (message && !STATUS_ONLY.test(message)) return message;
  if (status === 401) return 'Your session ended. Sign in again.';
  if (status === 403) return "You don't have access to this.";
  if (status === 404) return "We couldn't find that. It may have moved.";
  if (status === 429) return 'Too many tries. Wait a moment, then try again.';
  if (typeof status === 'number' && status >= 500) return "That's on us. Try again in a moment.";
  return fallback;
}

/** §10 state scenes in /illustrations (drawn by the illustrations group; read, never edited here). */
export const STATE_ART = /** @type {const} */ ({
  error: 'error-oops',
  offline: 'offline',
  notFound: 'not-found',
  noAccess: 'sign-in',
  comingSoon: 'rider-scooter',
  noTickets: 'no-tickets',
  noOrders: 'no-orders',
  noProducts: 'no-products',
  noRiders: 'no-riders',
  noResults: 'no-results',
});

/**
 * Which scene an error state shows: the rain cloud when the network is down, else the dropped skewer.
 * @param {{ status?: number; code?: string } | null | undefined} err
 */
export function errorArt(err) {
  return err && (err.status === 0 || err.code === 'network') ? STATE_ART.offline : STATE_ART.error;
}

/**
 * "Not here yet" copy for nav destinations that aren't built (BO-8): no phase numbers, no
 * jargon. Ops may pass its own note.
 * @param {string} title
 * @param {'ops' | 'shop'} scope
 * @param {string} [note]
 */
export function comingSoonCopy(title, scope, note) {
  return {
    caption: 'Not here yet',
    heading: `${title} is on the way`,
    body:
      note ||
      (scope === 'shop'
        ? "We're building this next. Today's orders all work from Today."
        : "We're building this next. Live orders, riders and shops all work today."),
  };
}

/**
 * Sales-by-line bar fill (BO-5): the §07 category colour; Express is Pop Yellow.
 * @param {string} line
 */
export function salesBarClass(line) {
  if (line === 'fresh' || line === 'grill' || line === 'mart') return `bar-${line}`;
  if (line === 'express' || line === 'express_surcharge') return 'bar-express';
  return 'bar-other';
}
